import type { FC, PropsWithChildren, ReactNode } from 'react';
import { styled } from '@mui/material';
import type { SxProps } from '@mui/material';

// https://martinwolf.org/before-2018/blog/2015/01/pure-css-savingloading-dots-animation/
const DotsLoadingIndicatorContainer = styled('span')({
    '@keyframes DotsLoadingIndicator': {
        /**
         * At the start of the animation the dot
         * has an opacity of .2
         */
        '0%': {
            opacity: 0.2,
        },
        /**
         * At 20% the dot is fully visible and
         * then fades out slowly
         */
        '20%': {
            opacity: 1,
        },
        /**
         * Until it reaches an opacity of .2 and
         * the animation can start again
         */
        '100%': {
            opacity: 0.2,
        },
    },
    whiteSpace: 'nowrap',
    '&>span': {
        animationName: 'DotsLoadingIndicator',
        animationDuration: '1.4s',
        animationIterationCount: 'infinite',
        /**
         * This makes sure that the starting style (opacity: .2)
         * of the animation is applied before the animation starts.
         * Otherwise we would see a short flash or would have
         * to set the default styling of the dots to the same
         * as the animation. Same applies for the ending styles.
         */
        animationFillMode: 'both',
    },
    '&>span:nth-of-type(2)': {
        animationDelay: '.2s',
    },
    '&>span:nth-of-type(3)': {
        animationDelay: '.4s',
    },
});

export type DotsLoadingIndicatorProps = PropsWithChildren<{
    sx?: SxProps;
    dot?: ReactNode;
}>;

export const DotsLoadingIndicator: FC<DotsLoadingIndicatorProps> = (props) => {
    const { dot = '.' } = props;
    if (props.children) {
        return (
            <span role="progressbar">
                <DotsLoadingIndicator sx={{ visibility: 'hidden' }} />
                {props.children}
                <DotsLoadingIndicator sx={props.sx} />
            </span>
        );
    }
    return (
        <DotsLoadingIndicatorContainer sx={props.sx} role="progressbar">
            <span>{dot}</span>
            <span>{dot}</span>
            <span>{dot}</span>
        </DotsLoadingIndicatorContainer>
    );
};

export default DotsLoadingIndicator;
